import { useCallback, useEffect, useMemo, useState } from 'react';
import './App.css';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import AppNavbar from './AppNavbar.js';
import SectionCard from './SectionCard.js';
import MarketWorkspaceTabs from './MarketWorkspaceTabs.js';
import OrdersTable from './OrdersTable.js';
import { catalogFetch } from './catalogApi.js';
import {
  buildLoginUrl,
  buildLogoutUrl,
  buildSignupUrl,
  getTokenStorageKey,
  isTokenExpired,
  readTokenFromUrlHash,
} from './auth.js';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || "";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
const COGNITO_REDIRECT_URI = `${window.location.origin}/`;
const COGNITO_LOGOUT_URI = `${window.location.origin}/`;

function isMarketOpen(market) {
  return String(market?.status || "").toUpperCase() === "OPEN";
}

function AppMain() {
  const [idToken, setIdToken] = useState("");
  const [me, setMe] = useState(null);
  const [balance, setBalance] = useState({ cash_available: 0, cash_reserved: 0 });
  const [orders, setOrders] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedMarketId, setSelectedMarketId] = useState("");
  const [marketWorkspaceTab, setMarketWorkspaceTab] = useState("open-orders");
  const [dashboardTab, setDashboardTab] = useState("markets");
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [marketError, setMarketError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const tokenFromHash = readTokenFromUrlHash();
    const storedToken = localStorage.getItem(getTokenStorageKey()) || "";
    const token = tokenFromHash || storedToken;

    if (!token || isTokenExpired(token)) {
      localStorage.removeItem(getTokenStorageKey());
      setIdToken("");
      return;
    }

    localStorage.setItem(getTokenStorageKey(), token);
    setIdToken(token);
    if (tokenFromHash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loginUrl = useMemo(
    () => buildLoginUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const signupUrl = useMemo(
    () => buildSignupUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const logoutUrl = useMemo(
    () => buildLogoutUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_LOGOUT_URI),
    []
  );

  const marketGroups = useMemo(() => {
    const grouped = new Map();
    markets.forEach((market) => {
      if (!market || !market.market_id) {
        return;
      }
      const groupId = market.group_id || market.market_id;
      const groupTitle = market.group_title || market.title || groupId;
      if (!grouped.has(groupId)) {
        grouped.set(groupId, {
          group_id: groupId,
          group_title: groupTitle,
          category: market.category || "binary",
          community_ids: Array.isArray(market.community_ids) ? market.community_ids : [],
          items: [],
        });
      }
      grouped.get(groupId).items.push(market);
    });

    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        items: [...group.items].sort((left, right) => {
          const leftOrder = Number.isInteger(left.group_order) ? left.group_order : Number.MAX_SAFE_INTEGER;
          const rightOrder = Number.isInteger(right.group_order) ? right.group_order : Number.MAX_SAFE_INTEGER;
          if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
          }
          return String(left.option_label || left.title || left.market_id).localeCompare(
            String(right.option_label || right.title || right.market_id)
          );
        }),
      }))
      .sort((a, b) => String(a.group_title).localeCompare(String(b.group_title)));
  }, [markets]);

  const selectedGroup = useMemo(
    () => marketGroups.find((group) => group.group_id === selectedGroupId) || null,
    [marketGroups, selectedGroupId]
  );

  const marketsInSelectedGroup = selectedGroup?.items || [];

  const refreshMarkets = useCallback(async () => {
    if (!idToken) {
      setMarkets([]);
      setSelectedGroupId("");
      setSelectedMarketId("");
      setMarketError("");
      return;
    }

    setLoadingMarkets(true);
    setMarketError("");
    try {
      const body = await catalogFetch("/markets", idToken);

      const items = Array.isArray(body.items) ? body.items : [];
      const validItems = items.filter((item) => item && item.market_id && isMarketOpen(item));
      setMarkets(validItems);
      const groups = new Map();
      validItems.forEach((item) => {
        groups.set(item.group_id || item.market_id, true);
      });
      setSelectedGroupId((current) => {
        if (current && groups.has(current)) {
          return current;
        }
        return validItems[0]?.group_id || validItems[0]?.market_id || "";
      });
      setSelectedMarketId((current) => {
        if (current && validItems.some((item) => item.market_id === current)) {
          return current;
        }
        return validItems[0]?.market_id || "";
      });
    } catch (err) {
      setMarketError(err.message || "Failed to load markets");
      setMarkets([]);
    } finally {
      setLoadingMarkets(false);
    }
  }, [idToken]);

  useEffect(() => {
    refreshMarkets();
  }, [refreshMarkets]);

  useEffect(() => {
    if (!marketGroups.length) {
      setSelectedGroupId("");
      setSelectedMarketId("");
      return;
    }

    const groupExists = marketGroups.some((group) => group.group_id === selectedGroupId);
    const nextGroupId = groupExists ? selectedGroupId : marketGroups[0].group_id;
    if (nextGroupId !== selectedGroupId) {
      setSelectedGroupId(nextGroupId);
      return;
    }

    const group = marketGroups.find((item) => item.group_id === nextGroupId);
    const groupMarkets = group?.items || [];
    const marketStillInGroup = groupMarkets.some((item) => item.market_id === selectedMarketId);
    if (!marketStillInGroup) {
      setSelectedMarketId(groupMarkets[0]?.market_id || "");
    }
  }, [marketGroups, selectedGroupId, selectedMarketId]);

  const apiFetch = useCallback(async (path, options = {}) => {
    if (!API_BASE_URL) {
      throw new Error("Missing REACT_APP_API_BASE_URL");
    }
    if (!idToken) {
      throw new Error("Not signed in");
    }
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
        ...(options.headers || {}),
      },
    });
    const body = await response.json();
    if (!response.ok) {
      throw new Error(body.error || body.message || "Request failed");
    }
    return body;
  }, [idToken]);

  const loadDashboard = useCallback(async () => {
    if (!idToken) {
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [meRes, balanceRes, ordersRes] = await Promise.all([
        apiFetch("/me"),
        apiFetch("/me/balance"),
        apiFetch("/me/orders?limit=100"),
      ]);
      setMe(meRes);
      setBalance(balanceRes);
      setOrders(ordersRes.items || []);
    } catch (err) {
      setError(err.message || "Failed to load account");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, idToken]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleLogin = () => {
    if (!loginUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(loginUrl);
  };

  const handleSignup = () => {
    if (!signupUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(signupUrl);
  };

  const handleLogout = () => {
    localStorage.removeItem(getTokenStorageKey());
    setIdToken("");
    setMe(null);
    setBalance({ cash_available: 0, cash_reserved: 0 });
    setOrders([]);
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  };

  const handlePlaceOrder = async (orderPayload) => {
    setSubmitting(true);
    setError("");
    try {
      await apiFetch("/orders", {
        method: "POST",
        body: JSON.stringify(orderPayload),
      });
      await Promise.all([loadDashboard(), refreshMarkets()]);
    } catch (err) {
      setError(err.message || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTakeOppositeOrder = async (order) => {
    const currentOutcome = String(order.outcome || "").toUpperCase();
    const oppositeOutcome = currentOutcome === "YES" ? "NO" : "YES";
    if (oppositeOutcome !== "YES" && oppositeOutcome !== "NO") {
      setError(`Cannot infer opposite outcome from: ${order.outcome}`);
      return;
    }

    const priceTicks = Number(order.limit_price);
    const remainingQty = Number(order.remaining_qty);
    const oppositePriceTicks = 100 - priceTicks;

    if (!Number.isInteger(priceTicks) || priceTicks <= 0 || priceTicks >= 100) {
      setError(`Invalid original order price: ${order.limit_price}`);
      return;
    }
    if (!Number.isInteger(remainingQty) || remainingQty <= 0) {
      setError(`Invalid remaining quantity: ${order.remaining_qty}`);
      return;
    }
    if (oppositePriceTicks <= 0 || oppositePriceTicks >= 100) {
      setError(`Cannot compute opposite price from: ${order.limit_price}`);
      return;
    }

    await handlePlaceOrder({
      market_id: order.market_id,
      outcome: oppositeOutcome,
      limit_price: oppositePriceTicks,
      qty: remainingQty,
    });
  };

  const handleCancelOrder = async (order) => {
    if (!order?.order_id) {
      setError("Missing order_id");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await apiFetch(`/orders/${encodeURIComponent(order.order_id)}`, {
        method: "DELETE",
      });
      await Promise.all([loadDashboard(), refreshMarkets()]);
    } catch (err) {
      setError(err.message || "Failed to cancel order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App">
      <AppNavbar
        links={[
            { href: "/", label: "Home" },
            { href: "/open-orders", label: "Open Orders" },
            { href: "/communities", label: "Communities" },
            { href: "/leaderboard", label: "Leaderboard" },
            { href: "/market-resolution", label: "Resolve Market" },
            { href: "/markets/new", label: "Create Market" },
          ]}
          isSignedIn={Boolean(idToken)}
          me={me}
          balance={balance}
          onLogin={handleLogin}
          onLogout={handleLogout}
          onSignup={handleSignup}
        />
      <Container className="py-4">

        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" className="mb-0">{error}</Alert>
            </Col>
          </Row>
        )}

        {!idToken ? (
          <Row>
            <Col>
              <Alert variant="info" className="mb-0">
                Sign in with Cognito to place orders and view your balance and order history.
              </Alert>
            </Col>
          </Row>
        ) : (
          <>
            <Row className="mb-4">
              <Col>
                <SectionCard className="marketViewer">
                  <Tabs
                    activeKey={dashboardTab}
                    onSelect={(key) => setDashboardTab(key || "markets")}
                    className="mb-3"
                  >
                    <Tab eventKey="markets" title="Markets">
                      {marketError && (
                        <Alert variant="warning">{marketError}</Alert>
                      )}
                      <div className="market-group-selector-shell">
                        <div className="market-group-selector-copy">
                          <div className="market-group-selector-label">Market Group</div>
                          <div className="market-group-selector-caption">
                            Switch between questions and categorical groups.
                          </div>
                        </div>
                        <Form.Group className="mb-0 market-group-selector-control">
                          <Form.Select
                            value={selectedGroupId}
                            onChange={(e) => setSelectedGroupId(e.target.value)}
                            disabled={loadingMarkets || marketGroups.length === 0}
                          >
                            {marketGroups.length === 0 ? (
                              <option value="">
                                {loadingMarkets ? "Loading categories..." : "No categories available"}
                              </option>
                            ) : (
                              marketGroups.map((group) => (
                                <option key={group.group_id} value={group.group_id}>
                                  {group.group_title || group.group_id}
                                </option>
                              ))
                            )}
                          </Form.Select>
                        </Form.Group>
                      </div>
                      <Container>
                        <MarketWorkspaceTabs
                          markets={marketsInSelectedGroup}
                          selectedGroup={selectedGroup}
                          selectedMarketId={selectedMarketId}
                          onSelectMarket={setSelectedMarketId}
                          orders={orders}
                          authToken={idToken}
                          activeTab={marketWorkspaceTab}
                          disabled={submitting || loading}
                          loading={loading}
                          onSelectTab={setMarketWorkspaceTab}
                          onPlaceOrder={handlePlaceOrder}
                          onCancelOrder={handleCancelOrder}
                          onTakeOppositeOrder={handleTakeOppositeOrder}
                        />
                      </Container>
                    </Tab>
                    <Tab eventKey="all-orders" title="All Orders">
                      <div className="pt-3">
                        <OrdersTable
                          orders={orders}
                          authToken={idToken}
                          onCancelOrder={handleCancelOrder}
                          disabled={submitting || loading}
                          emptyMessage="You have not placed any orders yet"
                        />
                      </div>
                    </Tab>
                  </Tabs>
                </SectionCard>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
}

export default AppMain;
